import { useState, useEffect, useCallback } from "react";
//import { getAllPaymentFrequency, getAllInterestType, getAllInterestPerYear } from "app/views/loanmasternew/hooks/LoanTermsService";
import { GridRowModes, GridRowEditStopReasons } from '@mui/x-data-grid';
import useLoanFund from "app/views/loanfund/hooks/useLoanFund";
import dayjs from 'dayjs';
import { ConstructionOutlined } from "@mui/icons-material";
import { getAllPaymentFrequency, getAllInterestType, getAllInterestPerYear } from '../../loanmasternew/LoanTermsService';


const useAmortization = (initialRows, setRows, scheduleList, setFieldValue, rows, annualInterestRate) => {

    const [rowModesModel, setRowModesModel] = useState({});
    const [paymentFrequencyList, setPaymentFrequencyList] = useState([]);
    const [AmortizationRows, setAmortizationRows] = useState([]);

    const updatePageData = useCallback(() => {
        getAllPaymentFrequency().then(response => {
            setPaymentFrequencyList(response.data.paymentFrequencies || []);

        }).catch(error => {
            console.error('Failed to fetch payment frequency data', error);
        });
    });

    useEffect(() => {
        updatePageData();
    }, [updatePageData]);

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id) => () => {
        console.log('handleSaveClick called for id:', id);

        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: GridRowModes.View }
        }));

        const updatedRows = rows.map((row) =>
            row.id === id ? { ...row, isNew: false } : row
        );

        setRows(updatedRows);
        setFieldValue("scheduleList", updatedRows);
    };

    const handleDeleteClick = (id) => () => {
        setRows(initialRows.filter((row) => row.id !== id));
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = (newRow, paymentFrequency) => {
        const paymentsPerYear = getPaymentsPerYear(paymentFrequency);
        const oldRow = rows.find(row => row.id === newRow.id);
        const changedIndex = rows.findIndex(row => row.id === newRow.id);
        const paymentChanged = oldRow?.payment !== newRow.payment;
        const updatedRows = [...rows];

        if (!paymentChanged) {
            const updatedRow = { ...newRow, isNew: false };
            const updated = rows.map(row =>
                row.id === newRow.id ? updatedRow : row
            );
            setFieldValue('amortization.scheduleList', updated);
            setRows(updated);
            return updatedRow;
        }

        const newPayment = parseFloat(newRow.payment);
        const changedAnnualRate = parseFloat(newRow.interestRate ?? oldRow?.interestRate ?? annualInterestRate) || 0;
        const changedMonthlyRate = (changedAnnualRate / 100) / paymentsPerYear;

        for (let i = 0; i < changedIndex; i++) {
            updatedRows[i] = { ...rows[i] };
        }

        const priorBalance = changedIndex === 0
            ? parseFloat(newRow.balance)
            : parseFloat(rows[changedIndex - 1].balance) - parseFloat(rows[changedIndex - 1].principal);

        let runningBalance = parseFloat(priorBalance.toFixed(2));

        // Calculate changed row
        let interest = parseFloat((runningBalance * changedMonthlyRate).toFixed(2));
        let principal = parseFloat((newPayment - interest).toFixed(2));
        let payment = newPayment;

        if (principal > runningBalance) {
            principal = runningBalance;
            payment = interest + principal;
        }

        updatedRows[changedIndex] = {
            ...rows[changedIndex],
            payment: payment.toFixed(2),
            interest: interest.toFixed(2),
            principal: principal.toFixed(2),
            balance: runningBalance.toFixed(2),
            accruedinterest: Math.max(interest - payment, 0).toFixed(2),
            interestRate: changedAnnualRate,
            isNew: false,
        };

        runningBalance = parseFloat((runningBalance - principal).toFixed(2));

        // Recalculate subsequent rows
        for (let i = changedIndex + 1; i < updatedRows.length; i++) {
            if (runningBalance <= 0) {
                updatedRows[i] = {
                    ...updatedRows[i],
                    payment: "0.00",
                    interest: "0.00",
                    principal: "0.00",
                    balance: "0.00",
                    accruedinterest: "0.00",
                    interestRate: changedAnnualRate,
                    isNew: false,
                };
                continue;
            }

            interest = parseFloat((runningBalance * changedMonthlyRate).toFixed(2));
            principal = parseFloat((newPayment - interest).toFixed(2));
            payment = newPayment;

            // Fix here: if principal exceeds remaining balance OR last row, adjust payment
            if (principal > runningBalance || i === updatedRows.length - 1) {
                principal = runningBalance;
                payment = principal + interest;
            }

            updatedRows[i] = {
                ...updatedRows[i],
                payment: payment.toFixed(2),
                interest: interest.toFixed(2),
                principal: principal.toFixed(2),
                balance: runningBalance.toFixed(2),
                accruedinterest: Math.max(interest - payment, 0).toFixed(2),
                interestRate: changedAnnualRate,
                isNew: false,
            };

            runningBalance = parseFloat((runningBalance - principal).toFixed(2));
        }

        setFieldValue('amortization.scheduleList', updatedRows);
        setRows(updatedRows);
        return updatedRows[changedIndex];
    };


    const getPaymentsPerYear = (frequency) => {
        switch (frequency) {
            case 1: return 12; // Monthly
            case 2: return 4;  // Quarterly
            case 3: return 2;  // Semi-Annually
            case 4: return 1;  // Annually
            case 5: return 24; // Semi-Monthly
            case 6: return 26; // Biweekly
            case 7: return 52; // Weekly

            default:
                throw new Error('Invalid frequency value');
        }
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const getRowsThroughMaturity = (scheduleRows, maturityDate) => {
        const maturity = dayjs(maturityDate).startOf('day');

        return scheduleRows.filter((row) => (
            !dayjs(row.duedate).startOf('day').isAfter(maturity)
        ));
    };

    const applyBalloonPayment = (scheduleRows) => {
        if (scheduleRows.length === 0) return scheduleRows;

        const lastRecordIndex = scheduleRows.length - 1;
        const lastRecord = { ...scheduleRows[lastRecordIndex] };
        const remainingPrincipal = parseFloat(lastRecord.balance) || 0;
        const paidInterest = parseFloat(lastRecord.interest) || 0;

        lastRecord.principal = remainingPrincipal.toFixed(2);
        lastRecord.payment = (remainingPrincipal + paidInterest).toFixed(2);
        lastRecord.accruedinterest = '0.00';

        return [
            ...scheduleRows.slice(0, lastRecordIndex),
            lastRecord,
        ];
    };

    // start 

    const calculateAmortizationDaily = async (data) => {
        return new Promise((resolve, reject) => {
            try {

                const AmortizationRows = [];
                const PVal = parseFloat(data.amortization.loanamount);

                let totalMonths;

                if (data.amortization.paymentamount === 0) {
                    totalMonths = data.amortization.monthsamortized;
                }
                else {
                    totalMonths = data.amortization.noofpayments;
                }

                let frequency = 2; // Semi-annually
                let APR = parseFloat(data.amortization.interestrate) / 100; // Annual interest rate

                let Balance = PVal; // Initial balance
                const noteDateObj = new Date(data.amortization.approvalDate);
                const firstPayDueDateObj = new Date(data.amortization.firstDueDate);
                const dueDay = getDueDayAnchor(data.amortization.approvalDate, data.amortization.firstDueDate);
                let lngD, strD, finalDueDate, strM;

                switch (data.amortization.paymentFrequency) {
                    case 1: // Monthly
                        frequency = 12;
                        break;
                    case 2: // Quarterly
                        frequency = 4;
                        break;
                    case 3: // Semi-Annually
                        frequency = 2;
                        break;
                    case 4: // Annually
                        frequency = 1;
                        break;
                    case 5: // semi-monthly
                        frequency = 24;
                        break;
                    case 6: // Biweekly
                        frequency = 26;
                        break;
                    case 7: // weekly
                        frequency = 52;
                        break;
                    default:
                        throw new Error('Invalid payment frequency');
                }

                strM = frequency;

                // Calculate total payment periods
                const periods = (totalMonths / 12) * frequency;

                let payment;
                // Calculate fixed payment amount for semi-annual payments
                if (data.amortization.monthsamortized != 0)
                    payment = parseFloat(Math.abs(pmt(APR / frequency, periods, PVal)).toFixed(2));
                else
                    payment = data.amortization.paymentamount;

                if (strM !== 0) {
                    strD = dayjs(data.amortization.firstDueDate).format('MM/DD/YYYY');
                }

                //calculate the days 
                if (data.amortization.paymentFrequency === 4)  // anually 
                {
                    const noteYear = noteDateObj.getFullYear();
                    const firstPayYear = firstPayDueDateObj.getFullYear();

                    if (noteYear % 4 === 0 || firstPayYear % 4 === 0) {
                        lngD = 365;
                    } else {
                        lngD = Math.abs((firstPayDueDateObj - noteDateObj) / (1000 * 60 * 60 * 24));
                    }
                } else {
                    lngD = Math.abs((firstPayDueDateObj - noteDateObj) / (1000 * 60 * 60 * 24));
                }
                let prevDueDate;

                for (let period = 1; period <= periods; period++) {

                    let nxtDueDate = strD;
                    if (period == 1) { prevDueDate = dayjs(data.amortization.approvalDate); nxtDueDate = dayjs(data.amortization.firstDueDate) }
                    else { nxtDueDate = dayjs(strD) }
                    //console.log(prevDueDate)
                    //console.log(nxtDueDate)

                    let lngD = dayjs(nxtDueDate).diff(dayjs(prevDueDate), 'day');
                    //console.log(lngD)
                    const selectedInterestPayment = ((Balance * APR) / data.amortization.dailyValues) * lngD; // Approx. monthly interest for 360 days
                    let principalPayment = payment - selectedInterestPayment;

                    // Adjust last payment if principal exceeds balance
                    if (principalPayment > Balance) {
                        principalPayment = Balance;
                    }

                    if (Balance < 0) Balance = 0;

                    if (period == periods) {
                        let remaining = Balance - principalPayment;
                        payment = payment + remaining;
                        principalPayment = principalPayment + remaining;
                    }

                    // Add row to schedule
                    AmortizationRows.push({
                        id: period,
                        Pay: period,
                        duedate: strD,
                        balance: parseFloat(Balance).toFixed(2),
                        payment: parseFloat(payment).toFixed(2),
                        principal: parseFloat(principalPayment).toFixed(2),
                        interest: parseFloat(selectedInterestPayment).toFixed(2),
                        accruedinterest: Math.max(selectedInterestPayment - payment, 0).toFixed(2),
                        interestRate: data.amortization.interestrate
                    });

                    prevDueDate = strD;
                    Balance -= principalPayment;
                    strD = calculateNextDueDate(strD, strM, data.amortization.paymentDate1, data.amortization.paymentDate2, dueDay);

                }

                // balloon payment calculation
                const filteredData = getRowsThroughMaturity(
                    AmortizationRows,
                    data.amortization.maturityDate
                );

                resolve(applyBalloonPayment(filteredData));

            } catch (error) {
                reject(error);
            }
        });
    };

    const pmt = (APR, periods, principal) => {
        if (APR === 0) return principal / periods; // No interest case
        return (principal * APR) / (1 - Math.pow(1 + APR, -periods));
    };

    // Function to calculate amortization and return rows
    const calculateAmortization = async (data) => {

        return new Promise((resolve, reject) => {
            try {
                // Initialize AmortizationRows
                const AmortizationRows = [];
                // Clear existing rows (if any) and initialize
                setAmortizationRows([]);

                console.log('calculateAmortization data:', data);

                let P, Balance = 0.00;
                let strM, curI, TotPmts, payment;
                let PVal = parseFloat(data.amortization.loanamount);
                let APR = parseFloat(data.amortization.interestrate) / 100;
                const dueDay = getDueDayAnchor(data.amortization.approvalDate, data.amortization.firstDueDate);
                let frequency, period, strD, finalDueDate;

                // Determine frequency and APR adjustment based on payment frequency
                switch (data.amortization.paymentFrequency) {
                    case 1: // Monthly
                        frequency = 12;
                        APR = APR / 12;
                        break;
                    case 2: // Quarterly
                        frequency = 4;
                        APR = APR / 4;
                        break;
                    case 3: // Semi-Annually
                        frequency = 2;
                        APR = APR / 2;
                        break;
                    case 4: // Annually
                        frequency = 1;
                        APR = APR;
                        break;
                    case 5: // semi-monthly
                        frequency = 24;
                        APR = APR / 24;
                        break;
                    case 6: // Biweekly
                        frequency = 26;
                        APR = APR / 26;
                        break;
                    case 7: // weekly
                        frequency = 52;
                        APR = APR / 52;
                        break;
                    default:
                        throw new Error('Invalid payment frequency');
                }

                strM = frequency;

                if (strM !== 0) {
                    strD = dayjs(data.amortization.firstDueDate).format('MM/DD/YYYY');
                    finalDueDate = data.amortization.closingDate;
                    payment = data.amortization.paymentamount ?? 0;
                }

                // Calculate payment if not provided
                if (payment === 0) {
                    const periods = data.amortization.monthsamortized * (frequency / 12);
                    payment = parseFloat(Math.abs(pmt(APR, periods, PVal)).toFixed(2));
                    TotPmts = Math.ceil(periods);
                }
                else {
                    TotPmts = data.amortization.noofpayments;
                }

                for (let period = 1; period <= TotPmts; period++) {
                    if (period === 1) { Balance = Math.abs(PVal); }

                    let interest = (Balance * APR);

                    P = parseFloat(payment - interest).toFixed(2);
                    if (P > Balance) { P = Balance; }

                    // Log the calculated values for debugging
                    console.log(`Period ${period}:`, {
                        Balance: parseFloat(Balance).toFixed(2),
                        Payment: parseFloat(payment).toFixed(2),
                        Principal: parseFloat(P).toFixed(2),
                        Interest: parseFloat(interest).toFixed(2)
                    });

                    // Add row to schedule
                    AmortizationRows.push({
                        id: period,
                        Pay: period,
                        duedate: strD,
                        balance: parseFloat(Balance).toFixed(2),
                        payment: parseFloat(payment).toFixed(2),
                        principal: parseFloat(P).toFixed(2),
                        interest: parseFloat(interest).toFixed(2),
                        accruedinterest: Math.max(interest - payment, 0).toFixed(2),
                        interestRate: data.amortization.interestrate
                    });

                    strD = calculateNextDueDate(strD, strM, data.amortization.paymentDate1, data.amortization.paymentDate2, dueDay);

                    // Update balance
                    Balance = Balance - P;
                    if (Balance < 0) { Balance = 0; }
                    if (Balance < P) { payment = Balance; }
                }

                console.log('Final AmortizationRows:', AmortizationRows);

                // balloon payment calculation
                const filteredData = getRowsThroughMaturity(
                    AmortizationRows,
                    data.amortization.maturityDate
                );

                // Resolve with a payoff row when maturity precedes the amortization term.
                resolve(applyBalloonPayment(filteredData));
            } catch (error) {
                // Reject the promise if there's an error
                reject(error);
            }
        });
    };

    const pmtoriginal = (APR, periods, principal) => {
        if (APR === 0) return principal / periods; // No interest case
        return (principal * APR) / (1 - Math.pow(1 + APR, -periods));
    };


    // Helper function to calculate total number of periods
    const calculateNPer = (APR, payment, principal) => {
        if (APR === 0) return principal / payment; // No interest case
        return Math.log(payment / (payment - APR * principal)) / Math.log(1 + APR);
    };
    const getFrequencyValue = (frequency) => {
        switch (frequency) {
            case 12:
                return 1; // Monthly
            case 4:
                return 3; // Quarterly
            case 2:
                return 6; // Semi-Annually
            case 1:
                return 12; // Annually
            case 24: // Semimonthly
                return 15; // 15 days
            case 26: // Biweekly
                return 14; // 14 days
            case 52: // Weekly
                return 7; // 7 days
            default:
                throw new Error('Invalid frequency value');
        }
    };


    function getDueDayAnchor(approvalDate, firstDueDate) {
        const approval = dayjs(approvalDate);
        const firstDue = dayjs(firstDueDate);

        if (!firstDue.isValid()) return 1;
        if (!approval.isValid()) return firstDue.date();

        const firstDueDay = firstDue.date();
        const approvalDay = approval.date();
        const firstDueWasClamped = firstDueDay === firstDue.daysInMonth() && approvalDay > firstDueDay;

        return firstDueWasClamped ? approvalDay : firstDueDay;
    }

    function setClampedDay(date, day) {
        const requestedDay = Number(day);
        const validDay = Number.isFinite(requestedDay) && requestedDay > 0 ? requestedDay : date.date();

        return date.date(Math.min(validDay, date.daysInMonth()));
    }

    function calculateNextDueDate(strD, frequency, day1 = 1, day2 = 15, dueDay) {
        let date = dayjs(strD);

        switch (frequency) {
            case 12:
            case 4:
            case 2:
            case 1:
                // Monthly, Quarterly, Semi-Annually, Annually
                date = date.startOf('month').add(getFrequencyValue(frequency), 'month');
                date = setClampedDay(date, dueDay);
                break;

            case 24: // Semimonthly
                const monthStart = date.startOf('month');
                const firstPaymentDate = setClampedDay(monthStart, day1);
                const secondPaymentDate = setClampedDay(monthStart, day2);

                if (date.isBefore(firstPaymentDate, 'day')) {
                    date = firstPaymentDate;
                } else if (date.isBefore(secondPaymentDate, 'day')) {
                    date = secondPaymentDate;
                } else {
                    date = setClampedDay(monthStart.add(1, 'month'), day1);
                }
                break;

            case 26: // Biweekly
                date = date.add(getFrequencyValue(26), 'day');
                break;

            case 52: // Weekly
                date = date.add(getFrequencyValue(52), 'day');
                break;

            default:
                throw new Error('Unsupported frequency');
        }

        return date.format('MM/DD/YYYY');
    }

    return {
        initialRows,
        rowModesModel,
        paymentFrequencyList,
        handleRowEditStop,
        handleEditClick,
        handleSaveClick,
        handleDeleteClick,
        handleCancelClick,
        processRowUpdate,
        handleRowModesModelChange,
        calculateAmortization,
        calculateAmortizationDaily,
        AmortizationRows,
        pmt,
    };
};

export default useAmortization;
